'use client';
import React, { useState, useEffect, useRef } from 'react';

const NOTIFICATIONS = [
  "A growing company out of Indianapolis was funded for $100K to refinance existing debt.",
  "A service business from Orlando obtained $2.9M for equipment.",
  "A service business out of Charlotte obtained $20K for new equipment.",
  "A service business in Baltimore obtained $180K for equipment.",
  "A local business based in Portland applied for $35K for working capital.",
  "A small business owner in Miami is securing $50K to fund a marketing campaign.",
  "Someone based in Charlotte just secured $10K to fund a marketing campaign.",
  "A small business owner based in Los Angeles obtained $2.9M to stock up on inventory.",
  "A retail business in Denver just secured $75K for inventory restocking.",
  "A startup in Austin obtained $150K to expand their team.",
  "A restaurant owner in Chicago secured $45K for kitchen renovations.",
  "A trucking company in Dallas obtained $500K for fleet expansion.",
  "A medical practice in Phoenix secured $220K for new equipment.",
  "A construction firm in Seattle obtained $1.2M for a new project.",
  "A salon owner in Atlanta secured $30K for renovations and supplies.",
  "A tech startup in San Francisco obtained $800K for product development.",
  "A family-owned bakery in Boston secured $25K for new ovens.",
  "A landscaping company in Nashville obtained $60K for equipment.",
  "A fitness studio in San Diego secured $90K to open a second location.",
  "A plumbing company in Houston obtained $110K for new service vehicles.",
  "A clothing boutique in New York secured $40K for seasonal inventory.",
  "A dental office in Minneapolis obtained $300K for office expansion.",
  "A food truck owner in Las Vegas secured $18K for a new vehicle.",
  "A marketing agency in Tampa obtained $55K for working capital.",
  "A logistics company in Columbus secured $750K for warehouse upgrades.",
  "A pet grooming business in Portland obtained $22K for new equipment.",
  "A law firm in Washington D.C. secured $200K for office renovations.",
  "A catering company in New Orleans obtained $35K for event equipment.",
  "A photography studio in Kansas City secured $15K for camera gear.",
  "A roofing contractor in Detroit obtained $85K for materials and labor.",
  "A daycare center in Philadelphia secured $120K for facility improvements.",
  "A wine shop in San Jose obtained $50K for inventory expansion.",
  "A tutoring center in Raleigh secured $28K for new learning tools.",
  "A cleaning service in Memphis obtained $20K for equipment and supplies.",
];

export default function FundingNotification() {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNext = (index: number) => {
    setCurrentIndex(index);
    setAnimating(true);
    setVisible(true);

    // Hide after 6 seconds
    timerRef.current = setTimeout(() => {
      setAnimating(false);
      setTimeout(() => setVisible(false), 400);
    }, 6000);
  };

  useEffect(() => {
    // Show first notification after 3 seconds
    const initialDelay = setTimeout(() => {
      showNext(0);
    }, 3000);

    return () => {
      clearTimeout(initialDelay);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!visible && currentIndex >= 0) {
      const nextDelay = setTimeout(() => {
        const nextIndex = (currentIndex + 1) % NOTIFICATIONS.length;
        showNext(nextIndex);
      }, 10000);

      return () => clearTimeout(nextDelay);
    }
  }, [visible, currentIndex]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        maxWidth: '340px',
        width: 'calc(100vw - 48px)',
        transform: animating ? 'translateX(0)' : 'translateX(120%)',
        opacity: animating ? 1 : 0,
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      {/* Green accent bar */}
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #38a169, #48bb78)' }} />

      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Icon */}
        <div
          style={{
            flexShrink: 0,
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#f0fff4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
          </svg>
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#38a169', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
            Recent Funding
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#2d3748', lineHeight: 1.5 }}>
            {NOTIFICATIONS[currentIndex]}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={() => { setAnimating(false); setTimeout(() => setVisible(false), 400); }}
          style={{
            flexShrink: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            color: '#a0aec0',
            lineHeight: 1,
          }}
          aria-label="Dismiss notification"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
